import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { IndividualMessage } from "@/Common/Api/Communications/Dto/IndividualMessage";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { HighPriorityIndicator } from "@/Common/Components/IconControls/HighPriorityIndicator";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { DocumentProgressBarPopup } from "@/Common/Components/ProgressBar/DocumentProgressBarPopup";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { IndividualMessageNote } from "@/Common/Models/NotesPopup/IndividualMessageNote";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LoadMessageTaskViewResponse } from "@/Communications/Api/Dto/Responses/LoadMessageTaskViewResponse";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { IndividualDTO } from "@/Communications/Models/MessageOrTaskItem";
import { CreateNoteInView } from "@/Documents/Components/DocumentView/CreateNoteInView";
import { FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { TaskViewComponent, TaskViewModel } from "./TaskViewComponent";

import img_icon_erlenmeyer_bleu from "@/Globals/_images/icons/erlenmeyer_bleu.png";

interface IMessageTaskViewProps {
    model: LoadMessageTaskViewResponse;
    title: string;
    isLoading: boolean;
    errorMsg: string;
    showPatientBanner: boolean;
    showAddNoteControl: boolean;
    showFooterControls: boolean;
    showDeleteControl: boolean;
    showArchiveControl: boolean;
    className?: string;
    showFullWidth?: boolean;

    onNoteAdded?: (text: string) => void;

    // task related functions
    onTaskGroupTypeChanged?: (isGroup: boolean) => void;
    onTaskStatusChanged?: (statusId: string) => void;

    // buttons functions
    onDeleteClicked?: (id: string) => void;
    onArchiveClicked?: (id: string, archiveInSharedInbox: boolean) => void;
    onMarkAsUnreadClicked?: (messageId: string) => void;

    onForwardClicked?: () => void;
    onReplyAllClicked?: () => void;
    onReplyClicked?: () => void;

    setCurrentFile: (file: AttachedObjectResponse) => void;
}

class MessageTaskViewState {
    public showNoteEditField: boolean;
    public isSharedInboxChecked: boolean;
    public patientBannerModel: IPatientBannerModel;
    public isFaxValid: boolean;
    public clinicianInfoState: FaxAndSignatureClinicianSelectionState;
    public mergedFileName: string;
    public contactId: string;
    public previewMergedPdfPath: string;
    public canSendFaxes: boolean;

    public numberOfDocumentsGenerated: number;
    public numberOfDocumentsToGenerate: number;
}

class MessageTaskViewComponent extends BaseReactComponent<IMessageTaskViewProps, MessageTaskViewState> {
    constructor(props: IMessageTaskViewProps) {
        super(props);

        const initialState = this.initStateFromProps(this.props);
        initialState.canSendFaxes = cacheStore.canSendFaxes();
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: IMessageTaskViewProps): void {
        if (this.props !== prevProps) {
            const modelChange = this.props.model != prevProps.model;
            const patientChanged = this.getPatientContactId(this.props) != this.getPatientContactId(prevProps);

            if (modelChange || patientChanged)
                this.setState(this.initStateFromProps(this.props), () => this.loadPatientRamqInfo());

            this.mutateState(s => {
                s.contactId = this.getPatientContactId(this.props);
            });
        }
    }

    public initStateFromProps(props: IMessageTaskViewProps): MessageTaskViewState {
        let s = new MessageTaskViewState();
        s.showNoteEditField = false;
        s.isSharedInboxChecked = false;

        let defaultPatientBannerModel = Immutable.fromJS({
            Model: null,
            PatientDropdownVisible: false,
            Warnings: []
        });
        if (props.model && props.model.LinkedPatient)
            s.patientBannerModel = defaultPatientBannerModel.set("Model", MyleModelsPatientPatient.FromResponseData(props.model.LinkedPatient));

        return s;
    }

    public componentDidMount(): void {
        this.loadPatientRamqInfo();

        $(window).trigger("resize");
    }

    public getPatientContactId(props: IMessageTaskViewProps): string {
        return props.model && props.model.LinkedPatient && props.model.LinkedPatient.Individual && props.model.LinkedPatient.Individual.Contact_id;
    }

    private async loadPatientRamqInfo(): Promise<void> {
        if (!this.state.patientBannerModel?.get("Model"))
            return;

        const response = await Client.postAsync<RAMQInfo>(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: this.state.patientBannerModel.get("Model").IndividualResponse.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );

        if (response.isSuccess) {
            let data = new RAMQInfo().FromResponseData(response.data);
            let patient = this.state.patientBannerModel.get("Model");

            if (data)
                patient.setRamq(data);

            this.mutateState(s => s.patientBannerModel = s.patientBannerModel.set("Model", patient));
        }
    }

    public render(): JSX.Element {
        if (!this.props.model || !this.props.model.IndividualMessage)
            return this.renderEmpty(this.props.isLoading, this.props.errorMsg);

        const model = this.props.model;
        const individualMsg = model.IndividualMessage;

        const taskViewModel = new TaskViewModel(
            individualMsg.IsGroupTask,
            individualMsg.DueDate,
            model.IndividualMessageState ? model.IndividualMessageState.TaskStatusLookupId : "",
            model.AttachedLab
        );

        let bodyCssClass = this.props.showPatientBanner ? "col-lg-8" : "col-lg-12";
        let bodyHeight = this.props.showFooterControls ? "100%" : "80%";

        const person = cacheStore.GetCliniciansPersonsByContactId(individualMsg.FromContactId);

        return (
            <div id="posref">
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <p className="header-font-color">
                                {this.props.title}
                            </p>
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div className="col-xs" style={{ marginLeft: "15px" }}>
                                    <h3 className="thin-font flex-display">
                                        <div className="ellipsis-auto" title={individualMsg.Subject} style={{ maxWidth: "40vw" }}>{individualMsg.Subject}</div>
                                        {this.renderOverallStatus(individualMsg.OverallTaskStatusLookupId, individualMsg.DueDate)}
                                        {cacheStore.IsHighPriority(individualMsg.PriorityLookupId) && <HighPriorityIndicator style={{ marginLeft: "10px" }} />}
                                    </h3>
                                </div>
                                {this.props.isLoading && (
                                    <div className="" style={{ marginLeft: "30px", maxHeight: "35px", overflow: "hidden" }}>
                                        <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("LoadingLabel")} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="fxheight hide-overflow">
                    <div className={bodyCssClass} style={{ paddingLeft: "0px", paddingRight: "0px", height: "100%" }}>
                        <div className="myle-doc-tb main-content myle-form documents" style={{ maxHeight: bodyHeight, minHeight: bodyHeight, overflow: "hidden auto" }} data-cy="messageTaskView-messageDetail">
                            {this.props.errorMsg && (
                                <div className="row">
                                    <p style={{ color: "red", fontSize: "14px" }}>{this.props.errorMsg}</p>
                                </div>
                            )}
                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("FromLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    <div key="fromContact"> {person ? person.NameWithPrefix : ""} </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("ToM2Label")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    {this.renderToClinicians(model.ToClinicians)}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("AttachPatientLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    {this.renderLinkedPatientHyperLink(model.LinkedPatient)}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("SubjectLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer" style={{ maxWidth: "40vw" }}>
                                    {individualMsg.Subject}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("PriorityLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    {cacheStore.GetLocalizedValueByLookupId(individualMsg.PriorityLookupId)}
                                    {cacheStore.IsHighPriority(individualMsg.PriorityLookupId) && <HighPriorityIndicator style={{ marginLeft: "10px" }} />}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("MessageDateLabel")}</label>
                                </div>
                                <div className="col-lg-9 col-md-9 col-sm-9 form-field-answer">
                                    <div className="tbc" style={{ paddingRight: "15px" }}>
                                        <span>{individualMsg.StartDate ? moment(individualMsg.StartDate).format("YYYY-MM-DD HH:mm:ss") : moment(individualMsg.SendDate).format("YYYY-MM-DD HH:mm:ss")}</span>
                                    </div>
                                </div>
                            </div>

                            {individualMsg.IsTask && (
                                <TaskViewComponent
                                    model={taskViewModel}
                                    onGroupTaskSelectionChange={this.props.onTaskGroupTypeChanged}
                                    onTaskStatusSelectionChange={this.props.onTaskStatusChanged}
                                    currentUserStatusId={(model.ToClinicians || []).filter(p => p.Id == sessionStore.individualContactId).map(p => p.TaskStatusLookupId).firstOrDefault()}
                                />
                            )}

                            <div className="row">
                                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                    <label className="form-label">{cacheStore.GetString("BodyLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer" style={{ whiteSpace: "pre-wrap" }}>
                                    {JSXHelper.BreakStringIntoParagraphs(individualMsg.Body)}
                                </div>
                            </div>

                            <div className="row ">
                                <div className="col-lg-2 col-md-4  text-right-lg text-right-md">
                                    <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-5">
                                    {this.renderNoteControl(this.props.showAddNoteControl, this.state.showNoteEditField)}

                                    {this.renderNoteList(model.IndividualMessageNotes)}
                                </div>
                            </div>
                            {this.props.model.AttachedLab && (
                                <div className="row">
                                    <div className="col-lg-2 col-md-2 col-sm-2 text-right" />
                                    <div className="col-lg-7 col-md-5">
                                        <img
                                            onClick={() => this.onLabAttachementClick()}
                                            alt="TaskFolder"
                                            src={img_icon_erlenmeyer_bleu}
                                            style={{ cursor: "pointer" }}
                                            ref="imgfolder"
                                        />
                                        <a onClick={() => this.onLabAttachementClick()}>{cacheStore.GetString("GoToResultFromTaskLabel")}</a>
                                    </div>
                                </div>
                            )}
                            {!isArrayNullOrUndefinedOrEmpty(this.props.model.AttachedObjects) && (
                                <div className="row">
                                    <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                        <label className="form-label">{cacheStore.GetString("AttachmentsLabel")}</label>
                                    </div>
                                    <div className="col-lg-7 col-md-5">
                                        <div className="margin-md-bottom">
                                            {this.state.canSendFaxes && (
                                                <a
                                                    className="margin-lg-right"
                                                    onClick={() => ExportableFileDispatcher.openMultipleFilesSignFaxPrintPopup({
                                                        patientContactId: this.state.contactId,
                                                        files: this.props.model.AttachedObjects.map(p => ExportableFileHelper.fromAttachedObject(p)),
                                                        mutateState: mutation => this.mutateState(s => mutation(s)),
                                                        defaultContext: this.state.patientBannerModel.get("Model").IntervenorsOnFile,
                                                        useLabsSearch: true
                                                    })}
                                                >
                                                    {cacheStore.GetString("FaxVerbLabel")}
                                                </a>
                                            )}
                                            {this.state.numberOfDocumentsToGenerate > 0 && (
                                                <DocumentProgressBarPopup
                                                    maxValue={this.state.numberOfDocumentsToGenerate}
                                                    currentValue={this.state.numberOfDocumentsGenerated}
                                                />
                                            )}
                                            <a
                                                onClick={async () => {
                                                    const result = await ExportableFileHelper.mergeExportableFiles(
                                                        this.props.model.AttachedObjects.map(p => ExportableFileHelper.fromAttachedObject(p)),
                                                        mutation => this.mutateState(s => mutation(s))
                                                    );
                                                    result?.print();
                                                }}
                                            >
                                                {cacheStore.GetString("PrintLabel")}
                                            </a>
                                        </div>
                                        {this.renderAttachedObjects()}
                                    </div>
                                </div>
                            )}
                            {this.renderSharedInboxDetails(model.SharedInboxContacts, model.ToClinicians)}

                        </div>

                    </div>

                    {this.state.patientBannerModel &&
                        this.renderPatientBanner()
                    }
                </div>

                {this.props.showFooterControls && (
                    <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                        <div className="row">
                            {this.renderLeftSideControls(individualMsg, model.SharedInboxContacts, model.ToClinicians)}
                            {this.renderRightSideControls()}
                        </div>
                    </div>
                )}
            </div>

        );
    }

    private renderEmpty(isLoading: boolean, errorMsg: string): JSX.Element {
        return (
            <div>
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-xs-12">
                            <p className="header-font-color">
                                {this.props.title}
                            </p>
                            {isLoading &&
                                <InformationLoadingComponent cssClass="d-inline-block" smallVersion={true} customMessage={cacheStore.GetString("LoadingLabel")} />
                            }
                        </div>
                    </div>
                </div>
                <div className="fxheight hide-overflow">
                    <div className="col-lg-12">
                        <div className="myle-doc-tb main-content scrollable myle-form documents">
                            {errorMsg && (
                                <div className="row">
                                    <p style={{ color: "red", fontSize: "14px" }}>{errorMsg}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderOverallStatus(statusId: string, dueDate: Date): JSX.Element {
        if (!statusId)
            return null;

        const statusColor = CommunicationsHelper.determineOverallStatusColor(statusId, dueDate);
        const overallStatus = cacheStore.GetLocalizedValueByLookupId(statusId);
        return (
            <span> - <strong style={{ color: statusColor }} data-cy="messageTaskViewComponent-status">{overallStatus}</strong></span>
        );
    }

    public renderToClinicians(clinicians: IndividualDTO[]): JSX.Element[] {
        if (!clinicians || clinicians.length === 0)
            return [];

        const toContacts: JSX.Element[] = [];
        clinicians.forEach((indDTO, idx) => {
            if (indDTO.TaskStatusLookupId) {
                toContacts.push(
                    <div key={`toClinicians__${idx}`}>
                        {indDTO.FirstName} {indDTO.LastName} ({cacheStore.GetLocalizedValueByLookupId(indDTO.TaskStatusLookupId)
                            + (indDTO.LastStatusUpdatedDate ? ` ${moment(indDTO.LastStatusUpdatedDate).format("YYYY/MM/DD HH:mm:ss")}` : "")
                        }),
                    </div>
                );
            }
            else {
                toContacts.push(
                    <div key={`toClinicians__${idx}`}>
                        {indDTO.FirstName} {indDTO.LastName},
                    </div>
                );
            }
        });

        return toContacts;
    }

    public renderLinkedPatientHyperLink(patient: LoadPatientModel): JSX.Element {
        if (!patient || !patient.Individual)
            return null;

        const individual = patient.Individual;

        return (
            <HyperLinkControl
                key={`Patient__${individual.Contact_id}`}
                onChange={() => patientStore.setActivePatientUI(individual.Contact_id)}
                text={NameHelper.formatFullNameForHasName(individual, NameHelperFormat.FirstNameFirst)}
            />
        );
    }

    private renderPatientBanner(): JSX.Element {
        if (!this.props.showPatientBanner) {
            return (
                <div className="left-border col-lg-4 patient-view-panel-right patient-banner-form-wrapper">
                    <div className="myle-form scrollable" />
                </div>
            );
        }

        return (
            <div className="left-border col-lg-4 patient-view-panel-right patient-banner-form-wrapper">
                <div className="myle-form scrollable">
                    <PatientBannerVertical
                        model={this.state.patientBannerModel}
                    />
                </div>
            </div>
        );
    }

    private renderNoteList(notes: IndividualMessageNote[]): JSX.Element[] {
        if (!notes || notes.length === 0)
            return [];

        return notes.map((note, idx) => {
            const person = cacheStore.GetEmployeeByContactIdOrEmpty(note.ContactId);
            const docPrefix = person.PrefixLookup?.GetValue() ?? "";

            return (
                <div key={note.Id + "__" + idx} className="notes-collection-item">
                    <div className="memo-header">
                        <strong>{docPrefix} {person.LastName}, {person.FirstName}</strong>
                        <span> {moment(note.DateCreated).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(note.DateCreated).format("HH:mm:ss")}</span>
                    </div>
                    <div className="note-container">
                        {JSXHelper.BreakStringIntoParagraphs(note.Note)}
                    </div>
                </div>
            );
        });
    }

    private renderNoteControl(showNoteControl: boolean, showNoteEditField: boolean): JSX.Element {
        const noteWrapperClassName = this.props.className ?? "";
        if (!showNoteControl) return null;

        if (!showNoteEditField) {
            return (
                <div style={{ marginBottom: "30px" }}>
                    <a onClick={() => this.mutateState(s => s.showNoteEditField = true)}>{cacheStore.GetString("AddNoteLabel")}</a>
                </div>
            );
        }

        return (
            <div className={`row ${noteWrapperClassName}`}>
                <div className="col-lg-6 col-md-9 col-sm-12 full-Width">
                    <CreateNoteInView
                        addNoteOperation={LocalOperations.Unassigned.Unassigned}
                        hideControlOperation={LocalOperations.Unassigned.Unassigned}
                        addNoteCallback={note => this.handleAddNoteCallBack(note)}
                        hideControlCallback={() => this.mutateState(s => s.showNoteEditField = false)}
                        maxHeight="205px"
                        showFullWidth={this.props.showFullWidth}
                    />
                </div>
            </div>
        );
    }

    private renderLeftSideControls(
        individualMsg: IndividualMessage,
        sharedInboxClinicians: IndividualDTO[],
        toClinicans: IndividualDTO[]
    ): JSX.Element {
        if (!individualMsg)
            return null;

        let isSharedInbox = false;
        if (sharedInboxClinicians && sharedInboxClinicians.length > 0 &&
            toClinicans && !toClinicans.some(x => x.Id === sessionStore.individualContactId))
            isSharedInbox = true;

        const deleteBtnControl = (
            !this.props.showDeleteControl || isSharedInbox
                ? null
                : (
                    <ButtonControl
                        key={`delete__${individualMsg.Id}`}
                        className="btn btn-danger push-right-5"
                        onClick={() => this.handleOnDelete()}
                        id={individualMsg.Id}
                        label={cacheStore.GetString("DeleteLabel")}
                    />
                )
        );
        const archiveBtnControl = (
            !this.props.showArchiveControl
                ? null
                : (
                    <ButtonControl
                        key={`archive__${individualMsg.Id}`}
                        className="btn btn-primary push-right-5"
                        disabled={isSharedInbox && !this.state.isSharedInboxChecked}
                        onClick={() => this.handleOnArchive()}
                        id={individualMsg.Id}
                        label={cacheStore.GetString("ArchiveLabel")}
                    />
                )
        );
        let markAsUnreadBtnControl = (
            <ButtonControl
                key={`markasunread__${individualMsg.Id}`}
                className="btn btn-default push-right-5"
                onClick={() => this.handleMarkAsUnread()}
                id={individualMsg.Id}
                label={cacheStore.GetString("MarkAsUnreadLabel")}
                dataCy="messageTaskView-markAsUnread"
            />
        );

        return (
            <div className="col-lg-6 col-md-6 col-sm-6">
                {deleteBtnControl}
                {archiveBtnControl}
                {markAsUnreadBtnControl}
                {this.props.showArchiveControl && isSharedInbox && (
                    <strong>
                        <CheckboxControl
                            key="isSharedInboxChecked"
                            checked={this.state.isSharedInboxChecked}
                            onChange={() => this.mutateState(s => s.isSharedInboxChecked = !this.state.isSharedInboxChecked)}
                            label={cacheStore.GetString("SharedInboxCheckboxLabel")}
                        />
                    </strong>
                )}
            </div>
        );
    }

    private renderRightSideControls(): JSX.Element {
        return (
            <div className="col-lg-6 col-md-6 col-sm-6 flex-display flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="brand"
                        onClick={() => this.handleForwardClick()}
                        label={cacheStore.GetString("ForwardLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        onClick={() => this.handleReplyAllClick()}
                        label={cacheStore.GetString("ReplyToAllLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        onClick={() => this.handleReplyClick()}
                        label={cacheStore.GetString("ReplyLabel")}
                    />
                </ButtonGroup>
            </div>
        );
    }

    private renderSharedInboxDetails(sharedInboxClinicians: IndividualDTO[], toClinicans: IndividualDTO[]): JSX.Element {
        if (!sharedInboxClinicians || sharedInboxClinicians.length === 0)
            return null;

        if (toClinicans && toClinicans.some(x => x.Id === sessionStore.individualContactId))
            return null;

        return (
            <div className="row">
                <div className="col-lg-6 col-md-8 text-center">
                    <label>{cacheStore.GetString("SharedMessageDetected")}</label>
                    {sharedInboxClinicians.map((x, idx) => (
                        <p key={`inbox__${idx}`}>
                            <strong>{`${x.LastName}, ${x.FirstName}`}</strong>
                        </p>
                    ))}
                </div>
            </div>
        );
    }

    private renderAttachedObjects(): JSX.Element[] {
        if (isArrayNullOrUndefinedOrEmpty(this.props.model.AttachedObjects))
            return [];

        let array = this.props.model.AttachedObjects.map((object, idx) => (
            <div key={object.Id + "_" + idx}>
                <a onClick={() => this.props.setCurrentFile(object)} target="_blank">{this.resolveAttachedObjectDescription(object)}</a>
            </div>
        ));

        return array;
    }

    private resolveAttachedObjectDescription(attachedObject: AttachedObjectResponse): string {
        if (!IsNullOrWhiteSpace(attachedObject.Description))
            return attachedObject.Description;

        return FileSelectionHelper.determineTypeName(attachedObject.Type);
    }

    private handleForwardClick(): void {
        if (this.props.onForwardClicked)
            this.props.onForwardClicked();
    }

    private handleReplyAllClick(): void {
        if (this.props.onReplyAllClicked)
            this.props.onReplyAllClicked();
    }

    private handleReplyClick(): void {
        if (this.props.onReplyClicked)
            this.props.onReplyClicked();
    }

    private handleAddNoteCallBack(note: string): void {
        this.mutateState(
            s => s.showNoteEditField = false,
            () => note && this.props.onNoteAdded?.(note)
        );
    }

    private handleOnDelete(): void {
        let individualMsg = this.props.model.IndividualMessage;
        if (individualMsg && this.props.onDeleteClicked) {
            this.props.onDeleteClicked(individualMsg.Id);
        }
    }

    private handleOnArchive(): void {
        let individualMsg = this.props.model.IndividualMessage;
        if (individualMsg && this.props.onArchiveClicked) {
            this.props.onArchiveClicked(individualMsg.Id, this.state.isSharedInboxChecked);
        }
    }

    private handleMarkAsUnread(): void {
        const message = this.props.model.IndividualMessage;
        if (message && this.props.onMarkAsUnreadClicked)
            this.props.onMarkAsUnreadClicked(message.Id);
    }

    private onLabAttachementClick(): void {
        sessionStore.dispatch(
            LocalOperations.Misc.RedirectToTab,
            {
                value: this.props.model.AttachedLab,
                target: RedirectTo.Labs
            }
        );
    }
}

export { MessageTaskViewComponent };
